import { useState, useMemo, useEffect, useCallback, MouseEvent } from 'react';
import { Accept, useDropzone } from 'react-dropzone';
import { ControllerRenderProps, FieldError } from 'react-hook-form';
import {
  Grid2,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogTitle,
  FormControl,
  FormLabel,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { LoadingButton } from '@mui/lab';
import UploadIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhotoOutlinedIcon from '@mui/icons-material/PhotoOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';

import { GradientIcon } from '../components';
import { fileSize } from '../../src/utils/fileSize';
import type { File as FileResource } from '../types/File';

// Constant for maximum file size (500kb)
const UPLOAD_MAX_SIZE = 512000;

const defaultMimeTypes = {
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/pdf': ['.pdf'],
};

export interface FileUploaderFieldProps {
  id: string;
  name: string;
  label?: string;
  disabled: boolean;
  error?: FieldError;
  readOnly?: boolean;
  value: FileResource[] | null;
  allowMultiple?: boolean;
  acceptedMimeTypes?: Accept;
  onChange: ControllerRenderProps['onChange'];
}

/*
 * See the original file here:
 * project: enrolments-frontend
 * path: src/components/FormBuilder/fields/FileField.tsx
 *
 * TODO: Gather reusable logic with PictureUploaderField since they share a lot of logic and UI.
 */
export const FileUploaderField = ({
  id,
  label,
  disabled,
  value = [],
  onChange,
  error,
  name,
  readOnly,
  allowMultiple = false,
  acceptedMimeTypes = defaultMimeTypes,
}: FileUploaderFieldProps) => {
  const theme = useTheme();

  const [showConfirm, setShowConfirm] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [fileToDeleteId, setFileToDeleteId] = useState<string | null>(null);

  const acceptedExtsLabel = useMemo(() => {
    if (!acceptedMimeTypes) return '';

    const acceptedExts = Object.values(acceptedMimeTypes)
      .flat()
      .map((ext) => ext.replace('.', '').toUpperCase())
      .sort();

    if (!acceptedExts.length) return '';

    const lastExt = acceptedExts[acceptedExts.length - 1];
    return acceptedExts.length > 1 ? acceptedExts.slice(0, -1).join(', ') + ' or ' + lastExt : acceptedExts[0];
  }, [acceptedMimeTypes]);

  // Cleanup any remaining object URLs on unmount.
  useEffect(() => {
    return () => {
      (value || []).forEach((file) => URL.revokeObjectURL(file?.preview));
    };
  }, [value]);

  const handleFileRemove = useCallback((file: FileResource) => {
    setShowConfirm(true);
    setFileToDeleteId(file.id);
  }, []);

  const handleClose = useCallback(() => {
    setShowConfirm(false);
    setFileToDeleteId(null);
  }, []);

  const handleConfirmFileRemove = useCallback(() => {
    const fileRemoved = value?.find((file) => file.id === fileToDeleteId);

    if (fileRemoved) {
      URL.revokeObjectURL(fileRemoved?.preview);
    }

    const newFileList = (value || []).filter((file) => file.id !== fileToDeleteId);

    onChange(newFileList);
    setShowConfirm(false);
    setFileToDeleteId(null);
  }, [value, fileToDeleteId, onChange]);

  const handleFileDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (!acceptedFiles.length) return;
      setIsUploading(true);

      const previouslyUploadedFiles = allowMultiple ? [...(value || [])] : [];
      const newFiles = [...previouslyUploadedFiles];

      acceptedFiles.forEach((file) => {
        const fileObj: FileResource = {
          id,
          filename: file.name,
          name,
          size: file.size,
          mime: file.type,
          preview: URL.createObjectURL(file),
        };
        newFiles.push(fileObj);
      });

      // Simulate a short delay to mimic uploading progress.
      setTimeout(() => {
        setIsUploading(false);
        onChange(newFiles);
      }, 500);
    },
    [allowMultiple, value, id, name, onChange],
  );

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    multiple: allowMultiple,
    accept: acceptedMimeTypes,
    disabled: disabled || readOnly,
    maxSize: UPLOAD_MAX_SIZE,
    onDrop: handleFileDrop,
  });

  const dropzoneIconColor = isDragReject ? theme.palette.error.main : theme.palette.primary.dark;
  const dropzoneBorderColor = isDragReject || error ? theme.palette.error.main : isDragAccept ? theme.palette.primary.main : grey[400];
  const dropzoneBgColor = isDragAccept ? theme.palette.success.light : isDragReject ? theme.palette.error.light : 'transparent';

  return (
    <FormControl fullWidth>
      {Boolean(label) && (
        <FormLabel
          sx={{
            mb: 2,
            fontWeight: 600,
            color: theme.palette.text.primary,
            fontSize: theme.typography.subtitle1.fontSize,
          }}
          id={`${name}-label`}
        >
          {label}
        </FormLabel>
      )}

      <Grid2 container rowSpacing={1} columnSpacing={3}>
        <Grid2 size={{ xs: 12, lg: 6 }}>
          <Stack
            {...getRootProps()}
            minHeight="152px"
            alignItems="center"
            justifyContent="center"
            bgcolor={dropzoneBgColor}
            sx={{ cursor: 'pointer' }}
            borderRadius={theme.spacing(1)}
            border={`1px dashed ${dropzoneBorderColor}`}
          >
            <input {...getInputProps()} />

            <Stack alignItems="center" justifyContent="center" spacing={1}>
              {isUploading ? (
                <CircularProgress />
              ) : (
                <>
                  <Box pt={1} pb={0.5}>
                    <UploadIcon sx={{ color: dropzoneIconColor }} />
                  </Box>

                  {!isDragActive && (
                    <Box>
                      <Typography display="inline" color="primary" sx={{ textDecorationLine: 'underline' }}>
                        Click to upload
                      </Typography>
                      <Typography display="inline"> or drag and drop</Typography>
                    </Box>
                  )}

                  {isDragAccept && (
                    <Typography color={theme.palette.primary.dark} fontWeight="bold">
                      Drop file(s) here to upload
                    </Typography>
                  )}

                  {isDragReject && <Typography color="error">File type, size or count is not allowed</Typography>}

                  <Box pt={0.5}>
                    {acceptedExtsLabel && (
                      <Typography display="inline" variant="subtitle2" color="secondary">
                        {acceptedExtsLabel}{' '}
                      </Typography>
                    )}
                    <Typography display="inline" variant="subtitle2" color="secondary">
                      (max. 500kb)
                    </Typography>
                  </Box>
                </>
              )}
            </Stack>
          </Stack>
          {error && (
            <Typography variant="caption" color="error">
              {error!.message as string}
            </Typography>
          )}
        </Grid2>

        <Grid2 size={{ xs: 12, lg: 6 }}>
          {(value || []).map((file) =>
            file.id ? (
              <Upload
                key={file.id}
                size={file.size}
                type={file.mime}
                name={file.filename}
                canRemove={!disabled}
                preview={file.preview}
                onRemove={() => handleFileRemove(file)}
              />
            ) : null,
          )}
        </Grid2>
      </Grid2>

      <Dialog open={showConfirm} onClose={handleClose}>
        <DialogTitle variant="h4" component="h2">
          Are you sure you want to remove this file?
        </DialogTitle>
        <DialogActions>
          <LoadingButton variant="contained" color="error" onClick={handleConfirmFileRemove}>
            Remove
          </LoadingButton>
          <Button variant="outlined" color="warning" onClick={handleClose}>
            Keep
          </Button>
        </DialogActions>
      </Dialog>
    </FormControl>
  );
};

interface UploadProps {
  name: string;
  size: number;
  type: string;
  preview: string;
  canRemove: boolean;
  directLink?: boolean;
  onRemove: () => void;
}

const Upload = ({ canRemove, directLink = false, name, onRemove, preview, size, type }: UploadProps) => {
  const theme = useTheme();
  const [previewOpen, setPreviewOpen] = useState(false);

  // Determine file-type.
  const isImage = ['image/jpg', 'image/jpeg', 'image/png', 'image/bmp', 'image/webp'].includes(type);
  const isPdf = type === 'application/pdf';

  const handleRemove = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      onRemove();
    },
    [onRemove],
  );

  const handleImagePreview = useCallback(() => setPreviewOpen(true), []);
  const handleClosePreview = useCallback(() => setPreviewOpen(false), []);
  const handlePdfPreview = useCallback(() => window.open(preview, '_blank'), [preview]);

  const handleDownload = useCallback(() => {
    if (directLink) return;

    const link = document.createElement('a');

    link.href = preview;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [directLink, name, preview]);

  return (
    <>
      <Stack
        direction="row"
        sx={{ mb: 1, p: 2 }}
        alignItems="flex-start"
        justifyContent="space-between"
        borderRadius={theme.spacing(1)}
        border={`1px solid ${theme.palette.primary.main}`}
      >
        <Stack width="100%" direction="row" spacing={2} pb={1}>
          <GradientIcon color="primary" IconComponent={isImage ? PhotoOutlinedIcon : InsertDriveFileOutlinedIcon} />

          <Stack m={1} maxWidth="50%">
            <Typography
              sx={{
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              {name}
            </Typography>
            <Typography variant="subtitle2" color="secondary" fontSize="small">
              Uploaded - {fileSize(size, 2)}
            </Typography>
          </Stack>
        </Stack>

        <CheckCircleRoundedIcon color="success" />
      </Stack>
      <Stack direction="row" spacing={1}>
        {isImage && (
          <Tooltip title="Preview image">
            <Button color="secondary" variant="outlined" sx={{ minWidth: 'auto' }} aria-label={`View ${name}`} onClick={handleImagePreview}>
              <VisibilityOutlinedIcon />
            </Button>
          </Tooltip>
        )}

        {isPdf && (
          <Tooltip title="Preview in new tab">
            <Button color="primary" variant="outlined" sx={{ minWidth: 'auto' }} aria-label={`Open ${name} in new tab`} onClick={handlePdfPreview}>
              <OpenInNewIcon />
            </Button>
          </Tooltip>
        )}

        <Tooltip title="Download file">
          <Button
            color="primary"
            variant="outlined"
            rel="noopener noreferrer"
            sx={{ minWidth: 'auto' }}
            aria-label={`Download ${name}`}
            href={directLink ? preview : ''}
            target={directLink ? '_blank' : undefined}
            onClick={() => {
              if (!directLink) handleDownload();
            }}
          >
            <DownloadIcon />
          </Button>
        </Tooltip>

        {canRemove && (
          <Tooltip title="Remove file">
            <Button variant="outlined" aria-label="Remove file" onClick={handleRemove} color="error" sx={{ minWidth: 'auto' }}>
              <DeleteOutlinedIcon />
            </Button>
          </Tooltip>
        )}
      </Stack>

      <Dialog open={previewOpen} onClose={handleClosePreview} maxWidth="md" fullWidth>
        <DialogTitle>{name}</DialogTitle>
        {isImage && <Box component="img" src={preview} alt={name} sx={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }} />}
        <DialogActions>
          <IconButton onClick={handleClosePreview} color="primary">
            Close
          </IconButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
